import React, { useState, useRef, KeyboardEvent } from 'react'
import {
  Box,
  HStack,
  Input,
  Text,
  Icon,
  Wrap,
  WrapItem
} from '@chakra-ui/react'
import { FiX } from 'react-icons/fi'

// Simple email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface EmailChipInputProps {
  value: string[]
  onChange: (emails: string[]) => void
  placeholder?: string
  helperText?: string
}

export const EmailChipInput: React.FC<EmailChipInputProps> = ({
  value,
  onChange,
  placeholder = 'Type email and press Enter...',
  helperText = 'Press Enter to add an email'
}) => {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim().toLowerCase()
    
    if (!trimmedEmail) return

    // Validate email format
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Please enter a valid email address')
      return
    }

    // Check for duplicates
    if (value.includes(trimmedEmail)) {
      setError('This email is already added')
      return
    }

    setError(null)
    onChange([...value, trimmedEmail])
    setInputValue('')
  }

  const removeEmail = (emailToRemove: string) => {
    onChange(value.filter(email => email !== emailToRemove))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmail(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last email when backspace is pressed on empty input
      removeEmail(value[value.length - 1])
    }
  }

  const handleBlur = () => {
    // Add email on blur if there's content
    if (inputValue.trim()) {
      addEmail(inputValue)
    }
  }

  const handleContainerClick = () => {
    inputRef.current?.focus()
  }

  return (
    <Box>
      <Box
        borderWidth="1px"
        borderColor={error ? 'red.300' : 'gray.200'}
        borderRadius="md"
        p={2}
        bg="white"
        cursor="text"
        onClick={handleContainerClick}
        _focusWithin={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
        transition="all 0.2s"
        minH="42px"
      >
        <Wrap gap={2} align="center">
          {value.map((email) => (
            <WrapItem key={email}>
              <HStack
                bg="brand.50"
                color="brand.700"
                px={2}
                py={1}
                borderRadius="md"
                fontSize="sm"
                gap={1}
              >
                <Text>{email}</Text>
                <Box
                  as="button"
                  type="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    removeEmail(email)
                  }}
                  cursor="pointer"
                  _hover={{ color: 'brand.900' }}
                  display="flex"
                  alignItems="center"
                >
                  <Icon as={FiX} boxSize={3} />
                </Box>
              </HStack>
            </WrapItem>
          ))}
          <WrapItem flex={1} minW="150px">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value)
                setError(null)
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={value.length === 0 ? placeholder : 'Add another...'}
              variant="unstyled"
              size="sm"
              fontSize="sm"
              _placeholder={{ color: 'gray.400' }}
            />
          </WrapItem>
        </Wrap>
      </Box>
      {error ? (
        <Text fontSize="xs" color="red.500" mt={1}>
          {error}
        </Text>
      ) : (
        <Text fontSize="xs" color="gray.500" mt={1}>
          {helperText}
        </Text>
      )}
    </Box>
  )
}



